// Matching for the search boxes on the category form.
//
// They used to compare with a bare String.includes, so the case had to line up:
// "Inna (Manager) DigiCode" was found by typing `e` - the lowercase e in
// "manager" - and by `Inna`, but not by `inna`. Tags were worse: a user's tags
// arrive either as an array or as a comma separated string, and `includes` on
// an array wants a whole tag rather than part of one.

// Both sides of every comparison go through this, so neither the case nor the
// space either side of what was typed matters.
export const normalizeSearch = (value: string): string => value.trim().toLowerCase()

// "inna digi" is two things to look for, not one string to find. Splitting means
// the words can be typed in any order and with any spacing between them.
export const searchWords = (query: string): string[] =>
  normalizeSearch(query).split(/\s+/).filter(Boolean)

/**
 * Whether every word of the query appears somewhere in the given values. An
 * empty query matches nothing: the dropdowns only open once something has been
 * typed, so there is no "show me everything" case to serve.
 */
export const matchesQuery = (query: string, values: (string | null | undefined)[]): boolean => {
  const words = searchWords(query)
  if (words.length === 0) return false

  const haystack = values
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .map(normalizeSearch)

  return words.every(word => haystack.some(value => value.includes(word)))
}

/**
 * A user's tags as a list. The server sends them either as an array or as one
 * comma separated string, and both turn up in the same list.
 */
export const parseUserTags = (raw: unknown): string[] => {
  if (raw == null) return []
  const list = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : []
  return list.map(tag => (typeof tag === 'string' ? tag.trim() : '')).filter(Boolean)
}
